import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageSquare, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useConversations } from '@/hooks/useMessages';
import { formatRelativeTime, cn } from '@/lib/utils';
import { useState } from 'react';

export function MessagesPage() {
  const navigate = useNavigate();
  const { data: conversations, isLoading } = useConversations();
  const [search, setSearch] = useState('');

  const filtered = conversations?.filter((c) =>
    c.friendName.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 pb-20 sm:p-6 lg:pb-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Messages</h1>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {!filtered || filtered.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-16 text-center"
        >
          <MessageSquare className="mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="text-lg font-semibold">No conversations yet</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Add friends and start chatting!
          </p>
        </motion.div>
      ) : (
        <div className="space-y-2">
          {filtered.map((conversation, index) => (
            <motion.div
              key={conversation.friendId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card
                className={cn(
                  'cursor-pointer transition-colors hover:bg-accent',
                  conversation.unreadCount > 0 && 'border-primary/50'
                )}
                onClick={() => navigate(`/messages/${conversation.friendId}`)}
              >
                <CardContent className="flex items-center gap-3 p-4">
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={conversation.friendAvatar || undefined}
                      alt={conversation.friendName}
                    />
                    <AvatarFallback>
                      {conversation.friendName
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span
                        className={cn(
                          'text-sm font-medium truncate',
                          conversation.unreadCount > 0 && 'font-bold'
                        )}
                      >
                        {conversation.friendName}
                      </span>
                      {conversation.lastMessage && (
                        <span className="ml-2 flex-shrink-0 text-xs text-muted-foreground">
                          {formatRelativeTime(conversation.lastMessage.createdAt)}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <p
                        className={cn(
                          'truncate text-sm text-muted-foreground',
                          conversation.unreadCount > 0 &&
                            'font-medium text-foreground'
                        )}
                      >
                        {conversation.lastMessage
                          ? conversation.lastMessage.type && conversation.lastMessage.type !== 'text'
                            ? conversation.lastMessage.type === 'challenge_sent'
                              ? 'Sent a challenge'
                              : conversation.lastMessage.type === 'challenge_accepted'
                                ? 'Challenge accepted!'
                                : conversation.lastMessage.type === 'challenge_declined'
                                  ? 'Challenge declined'
                                  : conversation.lastMessage.type === 'challenge_countered'
                                    ? 'Countered the challenge'
                                    : conversation.lastMessage.type === 'challenge_expired'
                                      ? 'Challenge expired'
                                      : conversation.lastMessage.content
                            : conversation.lastMessage.content
                          : 'No messages yet'}
                      </p>
                      {conversation.unreadCount > 0 && (
                        <Badge
                          variant="default"
                          className="ml-2 flex-shrink-0 text-xs"
                        >
                          {conversation.unreadCount}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
